import { useEffect, useMemo, useState } from "react";
import { fz, toastAward } from "@/lib/fanzone";
import { toast } from "sonner";
import { bumpEngagement } from "@/lib/engagement";
import { ArrowLeftRight, Search, Gavel, Landmark } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const PICKS = ["2027 1st", "2027 2nd", "2027 3rd", "2027 4th", "2027 5th", "2027 6th", "2027 7th"];

const gradeColor = (g) => {
  const c = (g || "C")[0];
  if (c === "A") return "text-emerald-600";
  if (c === "B") return "text-titans-blue";
  if (c === "C") return "text-amber-500";
  return "text-titans-red";
};

function AssetList({ players, selected, onToggle, testPrefix }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return players;
    return players.filter((p) => p.name.toLowerCase().includes(t) || (p.position || "").toLowerCase() === t);
  }, [players, q]);
  return (
    <div>
      <div className="relative mb-2">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-titans-muted" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search players…"
          data-testid={`${testPrefix}-search`}
          className="w-full bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none pl-8 pr-3 py-2 font-body text-sm rounded-md"
        />
      </div>
      <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
        {filtered.map((p) => {
          const sel = selected.has(p.name);
          return (
            <button
              key={p.id}
              onClick={() => onToggle(p.name)}
              data-testid={`${testPrefix}-player-${p.id}`}
              className={`w-full flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left transition-colors ${
                sel ? "bg-titans-navy border-titans-navy text-white" : "border-[#E2E8F0] hover:border-titans-blue"
              }`}
            >
              <span className={`font-display text-[11px] w-9 text-center rounded px-1 ${sel ? "bg-white/15" : "bg-[#F1F5F9] text-titans-muted"}`}>{p.position}</span>
              <span className="font-cond uppercase tracking-wide text-xs flex-1 truncate">{p.name}</span>
              {p.age && <span className={`font-cond text-[10px] shrink-0 ${sel ? "text-white/60" : "text-titans-muted"}`}>{p.age}y</span>}
            </button>
          );
        })}
        {!filtered.length && <div className="text-titans-silver text-xs font-body py-3 text-center">No matches</div>}
      </div>
    </div>
  );
}

function PickChips({ selected, onToggle, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-3">
      {PICKS.map((p) => {
        const sel = selected.has(p);
        return (
          <button
            key={p}
            onClick={() => onToggle(p)}
            data-testid={`${testPrefix}-pick-${p.replace(/\s/g, "-")}`}
            className={`px-2.5 py-1 rounded-full border font-cond uppercase tracking-wider text-[10px] transition-colors ${
              sel ? "bg-titans-red border-titans-red text-white" : "border-[#CBD5E1] text-titans-silver hover:border-titans-red"
            }`}
          >
            {p}
          </button>
        );
      })}
    </div>
  );
}

const toggleIn = (set, v) => {
  const n = new Set(set);
  if (n.has(v)) n.delete(v); else n.add(v);
  return n;
};

export default function TradeMachine() {
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [titansPlayers, setTitansPlayers] = useState([]);
  const [otherPlayers, setOtherPlayers] = useState([]);
  const [sendPlayers, setSendPlayers] = useState(new Set());
  const [sendPicks, setSendPicks] = useState(new Set());
  const [recvPlayers, setRecvPlayers] = useState(new Set());
  const [recvPicks, setRecvPicks] = useState(new Set());
  const [verdict, setVerdict] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadRecent = () => fz.trade.recent().then(({ data }) => setRecent(data.trades || [])).catch(() => {});

  useEffect(() => {
    fz.trade.teams().then(({ data }) => setTeams(data.teams || [])).catch(() => {});
    fz.roster53.pool().then(({ data }) => setTitansPlayers((data.pool || []).map((p) => ({ id: p.id, name: p.name, position: p.position, age: p.age })))).catch(() => {});
    loadRecent();
  }, []);

  useEffect(() => {
    if (!teamId) { setOtherPlayers([]); return; }
    setRecvPlayers(new Set());
    fz.trade.roster(teamId).then(({ data }) => setOtherPlayers(data.players || [])).catch(() => {});
  }, [teamId]);

  const team = teams.find((t) => t.id === teamId);
  const sendCount = sendPlayers.size + sendPicks.size;
  const recvCount = recvPlayers.size + recvPicks.size;
  const ready = teamId && sendCount > 0 && recvCount > 0 && sendCount <= 8 && recvCount <= 8;

  const evaluate = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setVerdict(null);
    try {
      const { data } = await fz.trade.evaluate({
        team: team?.name || "",
        titans_send: [...sendPlayers],
        titans_receive: [...recvPlayers],
        picks_send: [...sendPicks],
        picks_receive: [...recvPicks],
      });
      setVerdict(data);
      if (data.award) toastAward(data.award);
      bumpEngagement("game", { dedupeKey: `trade:${new Date().toISOString().slice(0, 10)}` });
      loadRecent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The war room went quiet — try again");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="fz-trade">
      <div className="titans-card p-5 md:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <div className="flex items-center gap-2">
            <ArrowLeftRight size={16} className="text-titans-red" />
            <span className="font-display text-xl uppercase">Trade Machine</span>
          </div>
          <button
            onClick={evaluate}
            disabled={!ready || busy}
            data-testid="fz-trade-submit"
            className="bg-titans-red hover:bg-titans-red-hover text-white px-6 py-3 font-cond uppercase tracking-wider text-xs disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Gavel size={13} /> {busy ? "War room deliberating…" : "Get the Verdict"}
          </button>
        </div>
        <p className="text-titans-silver text-xs font-body mb-5">
          Build any Titans trade — players and 2027 picks both ways — and our front-office rubric grades it on the spot. 8 trades per day.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <div data-testid="fz-trade-send-panel">
            <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-red mb-2">
              Titans send · {sendCount} asset{sendCount === 1 ? "" : "s"}
            </div>
            <AssetList players={titansPlayers} selected={sendPlayers} onToggle={(n) => setSendPlayers((s) => toggleIn(s, n))} testPrefix="fz-trade-send" />
            <PickChips selected={sendPicks} onToggle={(p) => setSendPicks((s) => toggleIn(s, p))} testPrefix="fz-trade-send" />
          </div>

          <div data-testid="fz-trade-recv-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue">
                Titans receive · {recvCount} asset{recvCount === 1 ? "" : "s"}
              </span>
            </div>
            <select
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              data-testid="fz-trade-team-select"
              className="w-full mb-2 bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-3 py-2 font-cond uppercase tracking-wide text-sm rounded-md"
            >
              <option value="">Choose a trade partner…</option>
              {teams.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
            </select>
            {teamId ? (
              <>
                <AssetList players={otherPlayers} selected={recvPlayers} onToggle={(n) => setRecvPlayers((s) => toggleIn(s, n))} testPrefix="fz-trade-recv" />
                <PickChips selected={recvPicks} onToggle={(p) => setRecvPicks((s) => toggleIn(s, p))} testPrefix="fz-trade-recv" />
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-[#CBD5E1] py-10 text-center text-titans-silver text-xs font-body">
                Pick a team to load their roster and picks
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {verdict && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="titans-card p-5 md:p-6"
            data-testid="fz-trade-verdict"
          >
            <div className="flex items-start gap-5 flex-wrap">
              <div className="text-center shrink-0">
                <div className={`font-display text-7xl leading-none ${gradeColor(verdict.grade)}`} data-testid="fz-trade-grade">{verdict.grade}</div>
                <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver mt-1">Trade grade</div>
              </div>
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`font-display text-sm px-3 py-1 rounded uppercase ${verdict.verdict === "ACCEPTED" ? "bg-emerald-600 text-white" : "bg-titans-red text-white"}`}
                    data-testid="fz-trade-verdict-stamp"
                  >
                    {verdict.verdict === "ACCEPTED" ? `${verdict.team} accepts` : `${verdict.team} rejects`}
                  </span>
                  {typeof verdict.remaining_today === "number" && (
                    <span className="font-cond uppercase tracking-wider text-[9px] text-titans-muted">{verdict.remaining_today} trades left today</span>
                  )}
                </div>
                <h3 className="font-display text-2xl uppercase leading-tight mt-2">{verdict.headline}</h3>
                <p className="font-body text-sm leading-relaxed mt-2">{verdict.analysis}</p>
                <div className="grid sm:grid-cols-2 gap-2 mt-3">
                  <div className="rounded-lg bg-titans-blue/10 px-3 py-2 font-body text-xs"><span className="font-cond uppercase tracking-wider text-[9px] text-titans-blue block">Titans get</span>{verdict.titans_gain}</div>
                  <div className="rounded-lg bg-[#F1F5F9] px-3 py-2 font-body text-xs"><span className="font-cond uppercase tracking-wider text-[9px] text-titans-muted block">{verdict.team} gets</span>{verdict.partner_gain}</div>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between font-cond uppercase tracking-wider text-[9px] text-titans-silver mb-1">
                    <span>Fleeced</span><span>Even deal</span><span>Heist</span>
                  </div>
                  <div className="relative h-2.5 rounded-full overflow-hidden" style={{ background: "linear-gradient(90deg, #C8102E, #E2E8F0 50%, #059669)" }}>
                    <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-titans-navy border-2 border-white shadow" style={{ left: `calc(${verdict.fairness}% - 7px)` }} data-testid="fz-trade-fairness-marker" />
                  </div>
                  <div className="font-cond uppercase tracking-wider text-[9px] text-titans-muted mt-1 text-center">Titans side of the deal: {verdict.fairness}/100</div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {recent.length > 0 && (
        <div className="titans-card p-5 md:p-6" data-testid="fz-trade-recent">
          <div className="flex items-center gap-2 mb-3">
            <Landmark size={15} className="text-titans-blue" />
            <span className="font-display text-xl uppercase">Latest From the War Room</span>
          </div>
          <div className="space-y-2">
            {recent.map((t, i) => (
              <div key={i} className="rounded-lg border border-[#E2E8F0] px-3 py-2.5" data-testid={`fz-trade-recent-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-display text-lg w-8 text-center ${gradeColor(t.grade)}`}>{t.grade}</span>
                  <span className={`font-cond uppercase tracking-wider text-[9px] px-1.5 py-0.5 rounded ${t.verdict === "ACCEPTED" ? "bg-emerald-600/15 text-emerald-700" : "bg-titans-red/10 text-titans-red"}`}>{t.verdict}</span>
                  <span className="font-cond uppercase tracking-wide text-xs flex-1 truncate">{t.headline}</span>
                  <span className="font-cond uppercase tracking-wider text-[9px] text-titans-muted shrink-0">{t.by}</span>
                </div>
                <div className="font-body text-[11px] text-titans-silver mt-1 truncate">
                  Send: {t.titans_send.join(", ")} → {t.team} · Get: {t.titans_receive.join(", ")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
